import View from "./view.js";
import { esc } from "../escape.js";
import { DEFAULT_ITEM_IMAGE } from "../defaultImage.js";
import { currencySymbol } from "../currency.js";
import {
  emojiPanelMarkup,
  visualHiddenInputsMarkup,
  wireVisualPicker,
} from "../itemVisualPicker.js";

const TRASH_ICON = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="14" height="14" aria-hidden="true">
  <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/>
</svg>`;

class MenuEditView extends View {
  _parentElement = document.querySelector(".modal-parent");
  _formDiv = document.querySelector(".edit-form-parent");
  _submitHandler = null;
  _currentItemId = null;
  _invCtx = null; // { canManage, items, links, autoItem } for the Inventory section

  _showEditMenuForm(handler) {
    this._parentElement.addEventListener("click", (e) => {
      e.preventDefault();
      const card = e.target.closest(".card");
      if (!card) return;
      // In the catalogue's bulk-select mode, a card click toggles selection
      // (handled by menuListView) - don't open the edit modal.
      if (card.closest(".menu-modal--selecting")) return;
      card.classList.remove("pulse");
      void card.offsetWidth;
      card.classList.add("pulse");
      handler(card.dataset.id);
    });
  }

  _insertEditMenuMarkup(item, invCtx = null, taxCtx = null) {
    this._currentItemId = item._id;
    this._invCtx = invCtx;
    const visualMode = item.iconEmoji ? "emoji" : "photo";
    const markUp = `
<div class="edit-modal-overlay">
  <div class="edit-modal-card">
    <button class="edit-modal-close" aria-label="Close modal">&#x2715;</button>

    <form class="edit-item-form">
      <h2 class="edit-form-title">Edit Menu Item</h2>

      <!-- Top grid: photo (4×2) + Item Name, Category, Price, Status (2×1 each) -->
      <div class="edit-top-wrap">
        <!-- Photo column -->
        <div class="edit-photo-col">
          <div class="item-photo-panel">
            <div class="file-upload-preview-wrapper edit-photo-box${item.imageURL ? "" : " edit-photo-box--empty"}">
              <img src="${esc(item.imageURL || DEFAULT_ITEM_IMAGE)}" alt="${esc(item.itemName)}" class="edit-image-preview" />
              <input type="file" class="edit-image-input" name="image" accept="image/*" style="display:none;" />
              <span class="edit-image-overlay">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"/><circle cx="12" cy="13" r="3.5"/></svg>
                <span>${item.imageURL ? "Change photo" : "Add photo"}</span>
              </span>
            </div>
            <p class="edit-photo-hint">Tap or drop an image</p>
          </div>
        </div>
        <!-- Fields: two explicit rows so each row is its own flex context -->
        <div class="edit-fields-col">
          <div class="edit-fields-row">
            <label class="edit-field">
              Item Name
              <input type="text" name="itemName" value="${esc(item.itemName)}" />
            </label>
            <div class="edit-field">
              <span>Category</span>
              <div class="category-wrapper">
                <select class="edit-field-select" name="category"></select>
                <div class="new-category-row hidden">
                  <input type="text" class="new-category-field edit-new-category-input" placeholder="New category name" />
                  <button class="new-category-button" type="button">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M8 12h8"/><path d="M12 8v8"/></svg>
                    Add
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div class="edit-fields-row">
            <label class="edit-field">
              Price
              <div class="edit-price-wrap">
                <span class="edit-price-prefix">${currencySymbol()}</span>
                <input type="number" name="price" value="${item.price}" min="0" step="0.01" inputmode="decimal" />
              </div>
            </label>
            <label class="edit-field">
              Status
              <select name="status">
                <option value="Active" ${item.status === "active" ? "selected" : ""}>Active</option>
                <option value="Inactive" ${item.status === "inactive" ? "selected" : ""}>Inactive</option>
                <option value="Unavailable" ${item.status === "unavailable" ? "selected" : ""}>Unavailable</option>
              </select>
            </label>
          </div>
        </div>
      </div>

      ${emojiPanelMarkup({ selectedEmoji: item.iconEmoji, extraClass: "item-emoji-panel--edit" })}
      ${visualHiddenInputsMarkup({ mode: visualMode, selectedEmoji: item.iconEmoji })}

      <!-- Variants -->
      <div class="edit-variants-section">
        <div class="edit-variants-header">
          <div class="edit-toggle-row">
            <h3 class="edit-variants-title">Variants</h3>
            <label class="edit-switch">
              <input type="checkbox" name="hasVariants" ${item.hasVariants ? "checked" : ""} />
              <span class="edit-slider"></span>
            </label>
          </div>
          <button type="button" class="edit-add-variant-btn">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M8 12h8"/><path d="M12 8v8"/></svg>
            Add Variant Group
          </button>
        </div>

        ${
          item.hasVariants
            ? item.variants.map((variant, vIndex) => `
            <div class="edit-variant-group">
              <div class="edit-variant-header">
                <span class="edit-variant-group-title">Variant Group Label</span>
                <button type="button" class="edit-delete-variant-btn">&#x1F5D1; Remove Group</button>
              </div>
              <input type="text" class="edit-variant-label-input" name="variants[${vIndex}][optionLabel]" value="${esc(variant.optionLabel)}" placeholder="e.g. Size, Flavor, Add-ons…" />
              <div class="edit-variant-cols-header">
                <span>Option name</span>
                <span>Price <span class="pb-tip" tabindex="0" role="img" aria-label="More info" data-tip="Leave empty or enter 0 if this option has no extra charge">i</span></span>
              </div>
              <div class="edit-variant-options">
                ${variant.options.map((option, oIndex) => `
                  <div class="edit-variant-row">
                    <input type="text" name="variants[${vIndex}][options][${oIndex}][optionName]" placeholder="Option name" value="${esc(option.optionName)}" />
                    <input type="number" name="variants[${vIndex}][options][${oIndex}][optionPrice]" placeholder="Price" value="${option.optionPrice}" />
                    <button type="button" class="edit-delete-option-btn" aria-label="Remove option">${TRASH_ICON}</button>
                  </div>
                `).join("")}
              </div>
              <button type="button" class="edit-add-option-btn">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M8 12h8"/><path d="M12 8v8"/></svg>
                Add Option
              </button>
            </div>
          `).join("")
            : ""
        }
      </div>

      ${taxCtx?.enabled ? `
      <div class="edit-tax-section">
        <h3 class="edit-variants-title">Tax
          <span class="pb-tip" tabindex="0" role="img" aria-label="More info" data-tip="Exemptions are managed centrally in Receipt &amp; Tax so you can flag many products at once, by price or category.">i</span>
        </h3>
        <div class="edit-tax-row">
          <span class="edit-tax-badge${item.taxClass === "exempt" ? " edit-tax-badge--exempt" : ""}">${item.taxClass === "exempt" ? esc(taxCtx.exemptLabel) : "Taxable"}</span>
          <button type="button" class="edit-tax-manage-link">Manage in Receipt &amp; Tax &rarr;</button>
        </div>
      </div>` : ""}

      ${invCtx?.canManage ? this._inventorySectionMarkup(item, invCtx) : ""}

      <!-- Actions -->
      <div class="edit-form-actions">
        <button type="submit" class="edit-update-btn">Update Item</button>
        <button type="button" class="edit-delete-btn">${TRASH_ICON} Delete item</button>
      </div>
    </form>
  </div>
</div>
`;
    this._formDiv.innerHTML = "";
    this._formDiv.insertAdjacentHTML("beforeend", markUp);
    this._syncVariantsEmptyState();
    this._syncAllInvRows();
  }

  // ── Inventory (stock tracking) section ──────────────────────────────────────
  // Three modes: Not tracked / Simple count (auto 1:1 item) / Ingredients
  // (linked inventory items with per-sale amounts, optionally per-variant).
  // Inputs here carry NO name attributes so they never leak into the item
  // form's FormData - the controller reads them via getInventoryConfig().

  _inventorySectionMarkup(item, ctx) {
    const mode = ctx.autoItem ? "simple" : ctx.links.length ? "ingredients" : "none";
    const tab = (id, label) =>
      `<button type="button" class="invm-tab${mode === id ? " invm-tab--active" : ""}" data-invmode="${id}">${label}</button>`;

    // Group this product's links by inventory item: base qty + override rows.
    const byItem = new Map();
    ctx.links.forEach((l) => {
      if (!byItem.has(l.inventoryItemId)) byItem.set(l.inventoryItemId, { base: null, overrides: [] });
      const g = byItem.get(l.inventoryItemId);
      if (l.variantLabel == null) g.base = l;
      else g.overrides.push(l);
    });
    // In simple mode the single auto link is machinery, not an ingredient row.
    if (ctx.autoItem) byItem.delete(ctx.autoItem.id);

    const rows = [...byItem.entries()]
      .map(([invItemId, g]) => this._ingredientRowMarkup(item, ctx, invItemId, g))
      .join("");

    return `
      <div class="invm-section">
        <h3 class="edit-variants-title">Inventory
          <span class="pb-tip" tabindex="0" role="img" aria-label="More info" data-tip="Track stock for this product: a simple per-unit count, or ingredient amounts used per sale. Stock goes down automatically when the product sells.">i</span>
        </h3>
        <div class="invm-tabs">
          ${tab("none", "Not tracked")}
          ${tab("simple", "Simple count")}
          ${tab("ingredients", "Ingredients")}
        </div>

        <div class="invm-pane${mode === "simple" ? "" : " hidden"}" data-invpane="simple">
          <div class="invx-field">
            <label>On hand</label>
            <input type="number" class="invm-onhand" min="0" step="any" placeholder="0" value="${ctx.autoItem ? ctx.autoItem.quantity : ""}" />
          </div>
          <p class="invm-hint">1 sale = 1 unit. Changing this count is logged as a manual adjustment.</p>
        </div>

        <div class="invm-pane${mode === "ingredients" ? "" : " hidden"}" data-invpane="ingredients">
          <div class="invm-rows">${rows}</div>
          <button type="button" class="invm-add-row">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M8 12h8"/><path d="M12 8v8"/></svg>
            Add ingredient
          </button>
          <p class="invm-hint">Amounts are what ONE sale of this product uses. If a sale picks two options that both set a Custom amount for the same ingredient, those amounts add together.</p>
        </div>
      </div>`;
  }

  _ingredientRowMarkup(item, ctx, selectedId = "", group = { base: null, overrides: [] }) {
    // Auto "simple count" items belong to their own product - not pickable here.
    const pickable = ctx.items.filter((i) => !i.linkedMenuItemId || i.id === selectedId);
    const selected = ctx.items.find((i) => i.id === selectedId);
    const unit = selected?.unit ?? "";
    const overrideFor = (label, option) =>
      group.overrides.find((o) => o.variantLabel === label && o.variantOption === option);

    // Current variant options -> one row each with an explicit three-state
    // control: Base (no override stored), Custom (override with its own qty),
    // None (override stored with qty 0 - that option uses none of this item).
    // The "uses X" caption and the example line are filled by _syncInvRow.
    // Overrides pointing at options that no longer exist render as removable
    // orphan notices.
    const currentPairs = new Set();
    const ovrInputs = (item.variants ?? []).map((g) => (g.options ?? []).map((opt) => {
      currentPairs.add(`${g.optionLabel}∥${opt.optionName}`);
      const ovr = overrideFor(g.optionLabel, opt.optionName);
      const mode = !ovr ? "base" : ovr.qty > 0 ? "custom" : "none";
      const seg = (m, label) =>
        `<button type="button" class="invm-ovr-seg-btn${mode === m ? " invm-ovr-seg-btn--active" : ""}" data-setmode="${m}">${label}</button>`;
      return `
        <div class="invm-ovr" data-ovrmode="${mode}" data-label="${esc(g.optionLabel)}" data-option="${esc(opt.optionName)}">
          <span class="invm-ovr-name">${esc(g.optionLabel)}: ${esc(opt.optionName)}</span>
          <div class="invm-ovr-seg" role="group" aria-label="Amount used when ${esc(g.optionLabel)}: ${esc(opt.optionName)} is picked">
            ${seg("base", "Base")}${seg("custom", "Custom")}${seg("none", "Not used")}
          </div>
          <span class="invm-ovr-amt${mode === "custom" ? "" : " hidden"}">
            <input type="number" min="0" step="any" placeholder="0" aria-label="Custom amount" value="${mode === "custom" ? ovr.qty : ""}" />
            <span class="invm-ovr-unit">${esc(unit)}</span>
          </span>
          <span class="invm-ovr-resolved${mode === "custom" ? " hidden" : ""}"></span>
        </div>`;
    }).join("")).join("");

    // Live "if sold as ... uses X" preview - one picker per variant group,
    // result computed with the same rules the checkout resolver uses.
    const examplePickers = (item.variants ?? [])
      .filter((g) => (g.options ?? []).length)
      .map((g) => `
        <select class="invm-example-pick" data-label="${esc(g.optionLabel)}" aria-label="${esc(g.optionLabel)}">
          ${g.options.map((opt, i) => `<option value="${esc(opt.optionName)}"${i === 0 ? " selected" : ""}>${esc(opt.optionName)}</option>`).join("")}
        </select>`)
      .join("");
    const exampleRow = examplePickers
      ? `<div class="invm-example">
           <span class="invm-example-if">A sale with</span>
           ${examplePickers}
           <span class="invm-example-result"></span>
         </div>`
      : "";

    const orphans = group.overrides
      .filter((o) => !currentPairs.has(`${o.variantLabel}∥${o.variantOption}`))
      .map((o) => `
        <div class="invm-ovr invm-ovr--orphan" data-label="${esc(o.variantLabel)}" data-option="${esc(o.variantOption)}" data-qty="${o.qty}">
          <span>&#9888; ${esc(o.variantLabel)}: ${esc(o.variantOption)} (option no longer exists - the base qty is used)</span>
          <button type="button" class="invm-ovr-clear">Remove</button>
        </div>`)
      .join("");

    const hasVariantUi = (item.variants ?? []).length > 0 || orphans;

    return `
      <div class="invm-row">
        <div class="invm-row-main">
          <select class="invm-item-select">
            <option value="">Choose ingredient…</option>
            ${pickable.map((i) => `<option value="${i.id}"${i.id === selectedId ? " selected" : ""}>${esc(i.name)} (${esc(i.unit)})</option>`).join("")}
          </select>
          <input type="number" class="invm-qty" min="0" step="any" placeholder="Base qty" value="${group.base ? group.base.qty : ""}" />
          <span class="invm-unit">${esc(selected?.unit ?? "")}</span>
          <button type="button" class="invm-row-remove" aria-label="Remove ingredient">${TRASH_ICON}</button>
        </div>
        ${hasVariantUi ? `
        <button type="button" class="invm-ovr-toggle">Per-variant amounts <span class="invm-ovr-caret">&#9662;</span></button>
        <div class="invm-ovrs${group.overrides.length ? "" : " hidden"}">
          <p class="invm-hint invm-ovrs-hint"><strong>Choosing Base never adds extra.</strong> Each sale uses the base qty once - picking more options doesn't multiply it. Custom = this option uses a different amount instead of the base. Not used = this option skips this ingredient (e.g. no paper cup when "Serve in: Mug" is picked). Tip: if an ingredient is only needed for certain options (like oat milk), leave the base qty empty and set Custom on just those.</p>
          ${ovrInputs}${orphans}${exampleRow}
        </div>` : ""}
      </div>`;
  }

  _syncAllInvRows() {
    this._formDiv.querySelectorAll(".invm-row").forEach((r) => this._syncInvRow(r));
  }

  // Fills one ingredient row's live feedback from its current inputs: the
  // muted "uses X" caption on every Base/None option row, and the example
  // line's result. Mirrors model.js _resolveLineConsumption: overrides
  // (Custom/None) matching the picked options are summed and replace the
  // base; a Custom row with an empty input behaves like Base.
  _syncInvRow(row) {
    const unit = row.querySelector(".invm-unit")?.textContent.trim() ?? "";
    const withUnit = (q) => `${Math.round(q * 1000) / 1000}${unit ? ` ${unit}` : ""}`;
    const baseQty = parseFloat(row.querySelector(".invm-qty")?.value);
    const base = Number.isFinite(baseQty) && baseQty > 0 ? baseQty : 0;

    const ovrs = [...row.querySelectorAll(".invm-ovr:not(.invm-ovr--orphan)")];
    ovrs.forEach((ovr) => {
      const resolved = ovr.querySelector(".invm-ovr-resolved");
      if (!resolved) return;
      const mode = ovr.dataset.ovrmode;
      if (mode === "none") resolved.textContent = "not used when picked";
      else resolved.textContent = base > 0 ? `uses base: ${withUnit(base)}` : "uses none (no base set)";
    });

    const result = row.querySelector(".invm-example-result");
    if (!result) return;
    const picked = [...row.querySelectorAll(".invm-example-pick")].map((s) => ({
      label: s.dataset.label,
      option: s.value,
    }));
    let matchedSum = 0;
    let anyMatched = false;
    ovrs.forEach((ovr) => {
      if (!picked.some((p) => p.label === ovr.dataset.label && p.option === ovr.dataset.option)) return;
      const mode = ovr.dataset.ovrmode;
      if (mode === "none") { anyMatched = true; return; }
      if (mode !== "custom") return;
      const v = parseFloat(ovr.querySelector(".invm-ovr-amt input")?.value);
      if (Number.isFinite(v)) { anyMatched = true; matchedSum += Math.max(v, 0); }
    });
    const eff = anyMatched ? matchedSum : base;
    result.innerHTML = eff > 0
      ? `uses <b>${esc(withUnit(eff))}</b>`
      : `uses <b>none</b> of this ingredient`;
  }

  // Delegated interactions for the Inventory section (registered once at init,
  // like the variant handlers).
  _addHandlerInventorySection() {
    this._formDiv.addEventListener("click", (e) => {
      const tabBtn = e.target.closest(".invm-tab");
      if (tabBtn) {
        const section = tabBtn.closest(".invm-section");
        const current = section.querySelector(".invm-tab--active")?.dataset.invmode;
        const next = tabBtn.dataset.invmode;
        if (current === next) return;
        // Leaving simple mode deletes the auto stock item on save - confirm.
        if (current === "simple" && this._invCtx?.autoItem) {
          const ok = window.confirm(
            "Stop simple tracking? This product's automatic stock record and its history will be removed when you update the product.",
          );
          if (!ok) return;
        }
        section.querySelectorAll(".invm-tab").forEach((t) => t.classList.toggle("invm-tab--active", t === tabBtn));
        section.querySelectorAll(".invm-pane").forEach((p) => p.classList.toggle("hidden", p.dataset.invpane !== next));
        return;
      }

      const addBtn = e.target.closest(".invm-add-row");
      if (addBtn) {
        const item = { variants: this._invCtx?.itemVariants ?? [] };
        const rowsEl = addBtn.closest(".invm-pane").querySelector(".invm-rows");
        rowsEl.insertAdjacentHTML("beforeend", this._ingredientRowMarkup(item, this._invCtx));
        this._syncInvRow(rowsEl.lastElementChild);
        return;
      }

      const removeBtn = e.target.closest(".invm-row-remove");
      if (removeBtn) { removeBtn.closest(".invm-row").remove(); return; }

      // Base / Custom / None switch on one option row.
      const segBtn = e.target.closest(".invm-ovr-seg-btn");
      if (segBtn) {
        const ovr = segBtn.closest(".invm-ovr");
        const mode = segBtn.dataset.setmode;
        ovr.dataset.ovrmode = mode;
        ovr.querySelectorAll(".invm-ovr-seg-btn").forEach((b) =>
          b.classList.toggle("invm-ovr-seg-btn--active", b === segBtn));
        ovr.querySelector(".invm-ovr-amt")?.classList.toggle("hidden", mode !== "custom");
        ovr.querySelector(".invm-ovr-resolved")?.classList.toggle("hidden", mode === "custom");
        this._syncInvRow(ovr.closest(".invm-row"));
        if (mode === "custom") ovr.querySelector(".invm-ovr-amt input")?.focus();
        return;
      }

      const ovrToggle = e.target.closest(".invm-ovr-toggle");
      if (ovrToggle) {
        ovrToggle.closest(".invm-row").querySelector(".invm-ovrs")?.classList.toggle("hidden");
        return;
      }

      const orphanClear = e.target.closest(".invm-ovr-clear");
      if (orphanClear) { orphanClear.closest(".invm-ovr--orphan").remove(); return; }
    });

    // Picking an inventory item updates the row's unit captions; changing an
    // example picker recomputes that row's preview.
    this._formDiv.addEventListener("change", (e) => {
      if (e.target.closest(".invm-example-pick")) {
        this._syncInvRow(e.target.closest(".invm-row"));
        return;
      }
      const select = e.target.closest(".invm-item-select");
      if (!select) return;
      const inv = this._invCtx?.items.find((i) => i.id === select.value);
      const row = select.closest(".invm-row");
      const unitEl = row?.querySelector(".invm-unit");
      if (unitEl) unitEl.textContent = inv?.unit ?? "";
      row?.querySelectorAll(".invm-ovr-unit").forEach((u) => { u.textContent = inv?.unit ?? ""; });
      if (row) this._syncInvRow(row);
    });

    // Typing in the base amount or a Custom amount live-updates the captions.
    this._formDiv.addEventListener("input", (e) => {
      if (e.target.closest(".invm-qty") || e.target.closest(".invm-ovr-amt")) {
        const row = e.target.closest(".invm-row");
        if (row) this._syncInvRow(row);
      }
    });
  }

  // Serializes the Inventory section. null = section not rendered (caller
  // lacks manage_inventory) - the controller then skips the inventory save.
  getInventoryConfig() {
    const section = this._formDiv.querySelector(".invm-section");
    if (!section) return null;
    const mode = section.querySelector(".invm-tab--active")?.dataset.invmode ?? "none";

    if (mode === "simple") {
      const onHand = parseFloat(section.querySelector(".invm-onhand")?.value);
      return { mode, onHand: Number.isFinite(onHand) ? onHand : 0 };
    }
    if (mode !== "ingredients") return { mode: "none" };

    const links = [];
    section.querySelectorAll(".invm-row").forEach((row) => {
      const inventoryItemId = row.querySelector(".invm-item-select")?.value;
      if (!inventoryItemId) return;
      const baseQty = parseFloat(row.querySelector(".invm-qty")?.value);
      const hasBase = Number.isFinite(baseQty) && baseQty > 0;

      const overrideLinks = [];
      row.querySelectorAll(".invm-ovr").forEach((ovr) => {
        if (ovr.classList.contains("invm-ovr--orphan")) {
          // Orphans still present are preserved as-is (Remove deletes them).
          overrideLinks.push({
            inventoryItemId,
            qty: parseFloat(ovr.dataset.qty),
            variantLabel: ovr.dataset.label,
            variantOption: ovr.dataset.option,
          });
          return;
        }
        const push = (qty) => overrideLinks.push({
          inventoryItemId,
          qty,
          variantLabel: ovr.dataset.label,
          variantOption: ovr.dataset.option,
        });
        const mode = ovr.dataset.ovrmode;
        if (mode === "none") {
          // qty 0 = that option uses none of this item. Only meaningful when a
          // base exists to override; without one it's already "none".
          if (hasBase) push(0);
          return;
        }
        if (mode !== "custom") return; // Base = no override row stored
        const v = parseFloat(ovr.querySelector(".invm-ovr-amt input")?.value);
        if (!Number.isFinite(v)) return; // empty Custom behaves like Base
        push(Math.max(v, 0));
      });

      // A row is valid with a base amount, with positive overrides only
      // (variant-only consumption: nothing is used unless one of those options
      // is picked), or both. Skip rows that could never consume anything.
      if (!hasBase && !overrideLinks.some((l) => l.qty > 0)) return;
      if (hasBase) links.push({ inventoryItemId, qty: baseQty, variantLabel: null, variantOption: null });
      links.push(...overrideLinks);
    });
    return { mode, links };
  }

  // Show a helpful hint inside the variants box whenever there are no groups, so
  // it never reads as an empty tinted bar.
  _syncVariantsEmptyState() {
    const section = this._formDiv.querySelector(".edit-variants-section");
    if (!section) return;
    const hasGroups = section.querySelector(".edit-variant-group");
    const hint = section.querySelector(".edit-variants-empty");
    if (hasGroups) {
      hint?.remove();
      return;
    }
    if (!hint) {
      section.insertAdjacentHTML(
        "beforeend",
        `<p class="edit-variants-empty">No variants yet. Add sizes, flavors, or add-ons (e.g. Small / Medium / Large) and the cashier picks one at checkout.</p>`,
      );
    }
  }

  _mapMenuCategoriesMarkUp(categories, selectedCategory) {
    const selectEl = document.querySelector(".edit-field-select");

    // Start fresh
    selectEl.innerHTML = "";

    // Default option (only selected if no category exists)
    selectEl.insertAdjacentHTML(
      "beforeend",
      `<option value="" disabled ${!selectedCategory ? "selected" : ""}>
       Select category
     </option>`,
    );

    // Add actual categories
    const markup = categories
      .map(
        (cat) => `
        <option value="${esc(cat)}" ${cat === selectedCategory ? "selected" : ""}>
          ${esc(cat[0].toUpperCase() + cat.slice(1))}
        </option>
      `,
      )
      .join("");

    selectEl.insertAdjacentHTML("beforeend", markup);

    // Add "new category" option
    selectEl.insertAdjacentHTML(
      "beforeend",
      `<option value="new-category">Add new category</option>`,
    );
  }

  // Returns the first empty required input inside variant groups, or null if all valid
  _validateVariants(form) {
    const groups = form.querySelectorAll(".edit-variant-group");
    for (const group of groups) {
      const labelInput = group.querySelector(".edit-variant-label-input");
      if (labelInput && !labelInput.value.trim()) return labelInput;

      const optionNames = group.querySelectorAll('input[name*="[optionName]"]');
      for (const opt of optionNames) {
        if (!opt.value.trim()) return opt;
      }
    }
    return null;
  }

  _updateItemData(handler) {
    if (this._submitHandler) {
      this._formDiv.removeEventListener("submit", this._submitHandler);
    }

    // Clear error highlight as the user types
    this._formDiv.addEventListener("input", (e) => {
      e.target.closest(".edit-input-error")?.classList.remove("edit-input-error");
    }, { once: false });

    this._submitHandler = (e) => {
      e.preventDefault();
      const form = e.target.closest(".edit-item-form");
      if (!form) return;

      // Validate variant fields before saving
      const badInput = this._validateVariants(form);
      if (badInput) {
        badInput.classList.remove("edit-input-error"); // reset so animation re-triggers
        void badInput.offsetWidth;                     // force reflow
        badInput.classList.add("edit-input-error");
        badInput.scrollIntoView({ behavior: "smooth", block: "center" });
        badInput.focus();
        return; // block save
      }

      const formData = new FormData(form);
      const data = {};
      for (let [key, value] of formData.entries()) {
        data[key] = value;
      }

      this._formDiv.removeEventListener("submit", this._submitHandler);
      this._submitHandler = null;

      handler(data);
      const overlay = form.closest(".edit-modal-overlay");
      if (overlay) overlay.remove();
      // Success feedback is a toast fired by the controller after the update
      // actually saves (controlShowEditMenu), not a modal here.
    };

    this._formDiv.addEventListener("submit", this._submitHandler);
  }

  _updateImagePreview() {
    // Click image to open file dialog
    this._formDiv.addEventListener("click", (e) => {
      const wrapper = e.target.closest(".file-upload-preview-wrapper");
      if (!wrapper) return;

      const input = wrapper.querySelector(".edit-image-input");
      if (!input) return;

      input.click();
    });

    // Update preview when file selected
    this._formDiv.addEventListener("change", (e) => {
      const input = e.target.closest(".edit-image-input");
      if (!input) return;

      const file = input.files[0];
      if (!file) return;

      if (!file.type.startsWith("image/")) {
        input.value = "";
        return;
      }

      const preview = input
        .closest(".file-upload-preview-wrapper")
        .querySelector(".edit-image-preview");

      const reader = new FileReader();
      reader.onload = (e) => {
        preview.src = e.target.result;
        input.closest(".edit-photo-box")?.classList.remove("edit-photo-box--empty");
      };
      reader.readAsDataURL(file);
    });
  }

  // Photo/Emoji toggle + emoji picker - shared with the Add form via
  // itemVisualPicker.js, wired once here since _formDiv persists across
  // re-renders (like the other delegated handlers in this file).
  _wireVisualPicker() {
    wireVisualPicker(this._formDiv);
  }

  // Drag-and-drop onto the photo box (in addition to click-to-browse).
  _addPhotoDropZone() {
    const boxFrom = (e) => e.target.closest(".edit-photo-box");

    this._formDiv.addEventListener("dragover", (e) => {
      const box = boxFrom(e);
      if (!box) return;
      e.preventDefault();
      box.classList.add("edit-photo-box--dragover");
    });

    this._formDiv.addEventListener("dragleave", (e) => {
      const box = boxFrom(e);
      if (box && !box.contains(e.relatedTarget)) box.classList.remove("edit-photo-box--dragover");
    });

    this._formDiv.addEventListener("drop", (e) => {
      const box = boxFrom(e);
      if (!box) return;
      e.preventDefault();
      box.classList.remove("edit-photo-box--dragover");

      const file = e.dataTransfer?.files?.[0];
      if (!file || !file.type.startsWith("image/")) return;

      // Push the dropped file into the hidden input so the form submits it.
      // Assigning .files doesn't fire a native change event, so dispatch one -
      // itemVisualPicker's delegated listener needs it to flip imageMode to
      // "photo" (mirrors what the click-to-browse path gets for free).
      const input = box.querySelector(".edit-image-input");
      const dt = new DataTransfer();
      dt.items.add(file);
      input.files = dt.files;
      input.dispatchEvent(new Event("change", { bubbles: true }));

      const preview = box.querySelector(".edit-image-preview");
      const reader = new FileReader();
      reader.onload = (ev) => {
        preview.src = ev.target.result;
        box.classList.remove("edit-photo-box--empty");
      };
      reader.readAsDataURL(file);
    });
  }

  _addVariantGroup() {
    this._formDiv.addEventListener("click", (e) => {
      const btn = e.target.closest(".edit-add-variant-btn");
      if (!btn) return;

      const variantsSection = btn.closest(".edit-variants-section");
      if (!variantsSection) return;

      // Determine next index
      const currentGroups = variantsSection.querySelectorAll(
        ".edit-variant-group",
      );
      const vIndex = currentGroups.length;

      // Build markup for a new empty variant group
      const markup = `
      <div class="edit-variant-group">
        <div class="edit-variant-header">
          <span class="edit-variant-group-title">Variant Group Label</span>
          <button type="button" class="edit-delete-variant-btn">🗑 Remove Group</button>
        </div>
        <input type="text" class="edit-variant-label-input" name="variants[${vIndex}][optionLabel]" value="" placeholder="e.g. Size, Flavor, Add-ons…" />
        <div class="edit-variant-cols-header">
          <span>Option name</span>
          <span>Price <span class="pb-tip" tabindex="0" role="img" aria-label="More info" data-tip="Leave empty or enter 0 if this option has no extra charge">i</span></span>
        </div>
        <div class="edit-variant-options">
          <div class="edit-variant-row">
            <input
              type="text"
              name="variants[${vIndex}][options][0][optionName]"
              placeholder="Option name"
            />
            <input
              type="number"
              name="variants[${vIndex}][options][0][optionPrice]"
              placeholder="Price"
            />
            <button type="button" class="edit-delete-option-btn" aria-label="Remove option">${TRASH_ICON}</button>
          </div>
        </div>
        <button type="button" class="edit-add-option-btn">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M8 12h8"/><path d="M12 8v8"/></svg>
          Add Option
        </button>
      </div>
    `;

      // Insert new group at the bottom of variants section
      variantsSection.insertAdjacentHTML("beforeend", markup);
      this._syncVariantsEmptyState(); // drop the empty hint now that a group exists

      // Get the newly added group
      const newGroup = variantsSection.querySelector(
        ".edit-variant-group:last-child",
      );

      // Animate in
      newGroup.classList.add("edit-variant-group--entering");

      // Scroll smoothly to the new group
      newGroup.scrollIntoView({ behavior: "smooth", block: "center" });

      // Focus on the group name input
      const input = newGroup.querySelector(
        "input[name^='variants'][name$='[optionLabel]']",
      );
      if (input) input.focus();

      this._reindexVariants(); // make sure indices stay correct

      // Auto-enable the hasVariants toggle when a group is added
      const checkbox = this._formDiv.querySelector('[name="hasVariants"]');
      if (checkbox && !checkbox.checked) {
        checkbox.checked = true;
        const addBtn = this._formDiv.querySelector(".edit-add-variant-btn");
        if (addBtn) addBtn.style.display = "";
      }
    });
  }

  _addOption() {
    this._formDiv.addEventListener("click", (e) => {
      const btn = e.target.closest(".edit-add-option-btn");
      if (!btn) return;

      const variantGroup = btn.closest(".edit-variant-group");
      if (!variantGroup) return;

      const optionsContainer = variantGroup.querySelector(
        ".edit-variant-options",
      );
      if (!optionsContainer) return;

      // Determine next option index
      const currentOptions =
        optionsContainer.querySelectorAll(".edit-variant-row");
      const oIndex = currentOptions.length;

      // Determine vIndex for this variant group
      const groupInput = variantGroup.querySelector(
        "input[name^='variants'][name$='[optionLabel]']",
      );
      const vIndexMatch = groupInput.name.match(/variants\[(\d+)\]/);
      const vIndex = vIndexMatch ? vIndexMatch[1] : 0;

      // Build new option row
      const markup = `
      <div class="edit-variant-row">
        <input
          type="text"
          name="variants[${vIndex}][options][${oIndex}][optionName]"
          placeholder="Option name"
        />
        <input
          type="number"
          name="variants[${vIndex}][options][${oIndex}][optionPrice]"
          placeholder="Price"
        />
        <button type="button" class="edit-delete-option-btn" aria-label="Remove option">${TRASH_ICON}</button>
      </div>
    `;

      optionsContainer.insertAdjacentHTML("beforeend", markup);

      // Scroll to the new option
      const newOption = optionsContainer.querySelector(
        ".edit-variant-row:last-child",
      );

      // Animate in
      newOption.classList.add("edit-variant-row--entering");

      newOption.scrollIntoView({ behavior: "smooth", block: "center" });

      // Focus on the new option name input
      const input = newOption.querySelector("input[name$='[optionName]']");
      if (input) input.focus();

      this._reindexVariants(); // make sure names stay correct
    });
  }

  _deleteVariant() {
    this._formDiv.addEventListener("click", (e) => {
      const btn = e.target.closest(".edit-delete-variant-btn");
      if (!btn) return;

      const confirmDelete = window.confirm("Delete this variant group?");

      if (!confirmDelete) return;

      const group = btn.closest(".edit-variant-group");
      if (group) group.remove();

      this._reindexVariants();
      this._syncVariantsEmptyState();

      const remaining = this._formDiv.querySelectorAll(".edit-variant-group");
      if (remaining.length === 0) {
        const checkbox = this._formDiv.querySelector('[name="hasVariants"]');
        if (checkbox) checkbox.checked = false;
      }
    });
  }

  _reindexVariants() {
    const groups = this._formDiv.querySelectorAll(".edit-variant-group");

    groups.forEach((group, vIndex) => {
      // Update variant label input
      const labelInput = group.querySelector('input[name*="[optionLabel]"]');

      labelInput.name = `variants[${vIndex}][optionLabel]`;

      // Update option inputs
      const optionRows = group.querySelectorAll(".edit-variant-row");

      optionRows.forEach((row, oIndex) => {
        const nameInput = row.querySelector('input[name*="[optionName]"]');
        const priceInput = row.querySelector('input[name*="[optionPrice]"]');

        nameInput.name = `variants[${vIndex}][options][${oIndex}][optionName]`;
        priceInput.name = `variants[${vIndex}][options][${oIndex}][optionPrice]`;
      });
    });
  }

  _deleteOption() {
    this._formDiv.addEventListener("click", (e) => {
      const btn = e.target.closest(".edit-delete-option-btn");
      if (!btn) return;

      const row = btn.closest(".edit-variant-row");
      if (row) row.remove();

      this._reindexVariants();
    });
  }

  _addHandlerHasVariantsToggle() {
    this._formDiv.addEventListener("change", (e) => {
      const checkbox = e.target.closest('[name="hasVariants"]');
      if (!checkbox) return;

      const addBtn = this._formDiv.querySelector(".edit-add-variant-btn");

      if (checkbox.checked) {
        if (addBtn) addBtn.style.display = "";
        return;
      }

      const existingGroups = this._formDiv.querySelectorAll(".edit-variant-group");

      if (existingGroups.length === 0) {
        if (addBtn) addBtn.style.display = "none";
        return;
      }

      // Revert toggle until user confirms
      checkbox.checked = true;

      this._showVariantsToggleConfirm(() => {
        checkbox.checked = false;
        existingGroups.forEach((g) => g.remove());
        if (addBtn) addBtn.style.display = "none";
        this._syncVariantsEmptyState();
      });
    });
  }

  _showVariantsToggleConfirm(onConfirm) {
    const markup = `
      <div class="edit-confirm-overlay">
        <div class="edit-confirm-dialog">
          <p class="edit-confirm-msg">Toggling this off will remove all existing variants. Do you want to continue?</p>
          <div class="edit-confirm-actions">
            <button type="button" class="edit-confirm-cancel-btn">Cancel</button>
            <button type="button" class="edit-confirm-yes-btn">Continue</button>
          </div>
        </div>
      </div>
    `;

    const container = this._formDiv.querySelector(".edit-modal-card");
    container.insertAdjacentHTML("beforeend", markup);

    const overlay = container.querySelector(".edit-confirm-overlay");

    overlay
      .querySelector(".edit-confirm-yes-btn")
      .addEventListener("click", () => {
        overlay.remove();
        onConfirm();
      });

    overlay
      .querySelector(".edit-confirm-cancel-btn")
      .addEventListener("click", () => {
        overlay.remove();
      });
  }

  _addHandlerDeleteItem(handler) {
    this._formDiv.addEventListener("click", (e) => {
      const btn = e.target.closest(".edit-delete-btn");
      if (!btn) return;

      const confirmed = window.confirm(
        "Delete this item? This cannot be undone.",
      );
      if (!confirmed) return;

      const overlay = btn.closest(".edit-modal-overlay");
      if (overlay) overlay.remove();

      handler(this._currentItemId);
    });
  }

  // Deep-link out to the Receipt Adjustments panel's centralized exemption picker.
  // Closes this modal first (both live in .modal-parent's stacking chain) so
  // the panel that opens behind it reads cleanly.
  _addHandlerManageTax(handler) {
    this._formDiv.addEventListener("click", (e) => {
      const btn = e.target.closest(".edit-tax-manage-link");
      if (!btn) return;
      const overlay = btn.closest(".edit-modal-overlay");
      if (overlay) overlay.remove();
      handler(this._currentItemId);
    });
  }

  _closeModal() {
    this._formDiv.addEventListener("click", (e) => {
      const btn = e.target.closest(".edit-modal-close");
      if (!btn) return;
      const overlay = btn.closest(".edit-modal-overlay");
      if (!overlay) return;
      const card = overlay.querySelector(".edit-modal-card");
      if (card) card.classList.add("modal-exiting");
      setTimeout(() => overlay.remove(), 220);
    });
  }

  _newEditCategoryToggle(handler) {
    this._formDiv.addEventListener("change", (e) => {
      const select = e.target.closest(".edit-field-select");
      if (!select) return;
      const row = select
        .closest(".category-wrapper")
        ?.querySelector(".new-category-row");
      if (!row) return;
      if (select.value === "new-category") {
        row.classList.remove("hidden");
        row.querySelector(".new-category-field").focus();
      } else {
        row.classList.add("hidden");
      }
    });

    this._formDiv.addEventListener("click", (e) => {
      const btn = e.target.closest(".edit-field .new-category-button");
      if (!btn) return;
      const row = btn.closest(".new-category-row");
      const input = row.querySelector(".new-category-field");
      const name = input.value.trim();
      if (!name) {
        alert("Please enter a category name");
        return;
      }
      handler(name);
      input.value = "";
      row.classList.add("hidden");
    });

    this._formDiv.addEventListener("keydown", (e) => {
      if (e.key !== "Enter") return;
      const input = e.target.closest(".edit-new-category-input");
      if (!input) return;
      e.preventDefault();
      const row = input.closest(".new-category-row");
      const name = input.value.trim();
      if (!name) {
        alert("Please enter a category name");
        return;
      }
      handler(name);
      input.value = "";
      row.classList.add("hidden");
    });
  }
}

export default new MenuEditView();
